import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useBrand } from "@/context/BrandContext";
import { useT } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, KeyRound, MessageCircle, ArrowLeft, Eye, EyeOff } from "lucide-react";

export default function ForgotPassword() {
  const brand = useBrand();
  const { t } = useT();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [identifier, setIdentifier] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const isEmail = identifier.includes("@");

  const requestCode = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await api.post("/auth/forgot-password", { identifier: identifier.trim() });
      toast.success(isEmail ? t("auth.linkSent") : t("auth.codeSent"));
      setStep(2);
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const doReset = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    if (newPassword !== confirmPwd) { setError(t("auth.passwordMismatch")); setLoading(false); return; }
    try {
      await api.post("/auth/reset-password", { identifier: identifier.trim(), code: code.trim(), new_password: newPassword });
      toast.success(t("auth.resetDone"));
      navigate("/connexion");
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const waHref = `${brand.support}?text=${encodeURIComponent(`Bonjour, je souhaite réinitialiser mon mot de passe Ovolaay. Mon identifiant : ${identifier}`)}`;

  return (
    <div className="container-page py-12 flex justify-center">
      <div className="w-full max-w-md">
        <Link to="/connexion" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-secondary mb-4" data-testid="back-to-login"><ArrowLeft className="h-4 w-4" /> {t("auth.loginBtn")}</Link>
        <div className="text-center mb-6">
          <span className="inline-flex h-14 w-14 rounded-2xl bg-primary/10 items-center justify-center"><KeyRound className="h-7 w-7 text-primary" /></span>
          <h1 className="mt-4 font-heading font-extrabold text-2xl text-secondary">{t("auth.forgotTitle")}</h1>
          <p className="text-muted-foreground mt-1 text-sm">{t("auth.forgotSubtitle")}</p>
        </div>

        {step === 1 ? (
          <form onSubmit={requestCode} className="rounded-2xl bg-card border border-border p-6 space-y-4" data-testid="forgot-form">
            <div>
              <Label>{t("auth.identifier")}</Label>
              <Input required value={identifier} onChange={(e) => setIdentifier(e.target.value)} placeholder="email@exemple.com ou +223…" className="mt-1.5 h-12" data-testid="forgot-identifier" />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="forgot-error">{error}</p>}
            <Button type="submit" disabled={loading} className="w-full rounded-full bg-primary hover:bg-primary/90 h-11" data-testid="forgot-submit">
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{t("auth.sendCode")}
            </Button>
          </form>
        ) : (
          <form onSubmit={doReset} className="rounded-2xl bg-card border border-border p-6 space-y-4" data-testid="reset-form">
            {isEmail && (
              <div className="rounded-xl bg-secondary/10 text-secondary text-sm p-4" data-testid="email-link-sent">
                {t("auth.linkSentHint")}
              </div>
            )}
            <p className="text-sm text-muted-foreground">{t("auth.codeSentHint")}</p>
            <div>
              <Label>{t("auth.code")}</Label>
              <Input required value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" inputMode="numeric" className="mt-1.5 h-12 tracking-widest text-center text-lg" data-testid="reset-code" />
            </div>
            <div>
              <Label>{t("auth.newPassword")}</Label>
              <div className="relative mt-1.5">
                <Input required type={showPwd ? "text" : "password"} minLength={6} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="h-12 pr-11" data-testid="reset-new-password" />
                <button type="button" onClick={() => setShowPwd((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" data-testid="reset-toggle-password" aria-label="toggle">
                  {showPwd ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
            <div>
              <Label>{t("auth.confirmPassword")}</Label>
              <Input required type={showPwd ? "text" : "password"} minLength={6} value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} className="h-12 mt-1.5" data-testid="reset-confirm-password" />
              {confirmPwd && confirmPwd !== newPassword && <p className="text-xs text-destructive mt-1" data-testid="reset-mismatch">{t("auth.passwordMismatch")}</p>}
            </div>
            {error && <p className="text-sm text-destructive" data-testid="reset-error">{error}</p>}
            <Button type="submit" disabled={loading} className="w-full rounded-full bg-secondary hover:bg-secondary/90 h-11" data-testid="reset-submit">
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{t("auth.resetBtn")}
            </Button>
          </form>
        )}

        <a href={waHref} target="_blank" rel="noreferrer" className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-success px-4 py-2.5 text-sm font-bold text-white hover:opacity-90" data-testid="forgot-whatsapp">
          <MessageCircle className="h-4 w-4" /> {t("auth.noCodeWhatsapp")}
        </a>
      </div>
    </div>
  );
}
